// src/components/forms/FeedbackFormBuilder.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Container,
    Paper,
    Box,
    Typography,
    Grid,
    Button,
    TextField,
    Card,
    CardContent,
    CardActions,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    IconButton,
    Alert,
    Chip,
    Switch,
    Divider,
    Tabs,
    Tab,
    alpha,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    ArrowBack as BackIcon,
    CloudUpload as UploadIcon,
    Code as CodeIcon,
} from '@mui/icons-material';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import AdminLayout from '../pages/AdminLayout';

const FEEDBACK_TYPES = [
    { value: 'rating', label: 'Rating Scale (Stars)' },
    { value: 'nps', label: 'NPS Score (0-10)' },
    { value: 'text', label: 'Short Text' },
    { value: 'textarea', label: 'Long Text (Paragraph)' },
    { value: 'checkbox', label: 'Multiple Choice (Checkboxes)' },
    { value: 'radio', label: 'Single Choice (Radio)' },
    { value: 'dropdown', label: 'Dropdown Select' },
    { value: 'emoji', label: 'Emoji Rating' },
];

export default function FeedbackFormBuilder() {
    const { formId } = useParams();
    const navigate = useNavigate();
    const [isPublished, setIsPublished] = useState(false);
    const [formTitle, setFormTitle] = useState('');
    const [formDescription, setFormDescription] = useState('');
    const [category, setCategory] = useState('general');
    const [allowAnonymous, setAllowAnonymous] = useState(false);
    const [showProgressBar, setShowProgressBar] = useState(true);
    const [thankYouMessage, setThankYouMessage] = useState('Thank you for your feedback!');
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
    const [jsonInput, setJsonInput] = useState('');
    const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
    const [status, setStatus] = useState('inactive');
    const [tabValue, setTabValue] = useState(0);

    const [newQuestion, setNewQuestion] = useState({
        text: '',
        type: 'rating',
        required: false,
        scale: 5,
        category: 'general',
        placeholder: '',
        maxLength: 500,
        rows: 3,
        options: [],
        labels: {
            min: '',
            max: '',
        },
    });

    useEffect(() => {
        if (formId && formId !== 'new') {
            fetchForm();
        }
    }, [formId]);

    const fetchForm = async () => {
        try {
            setLoading(true);
            const formDoc = await getDoc(doc(db, 'forms', formId));
            if (formDoc.exists()) {
                const data = formDoc.data();
                setFormTitle(data.title || '');
                setFormDescription(data.description || '');
                setCategory(data.category || 'general');
                setAllowAnonymous(data.allowAnonymous ?? false);
                setShowProgressBar(data.showProgressBar ?? true);
                setThankYouMessage(data.thankYouMessage || 'Thank you for your feedback!');
                setQuestions(data.questions || []);
                setStatus(data.status || 'inactive');
                setIsPublished(data.isPublished ?? false);
            }
        } catch (err) {
            console.error('Error fetching form:', err);
            setError('Failed to load form: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveForm = async () => {
        try {
            setError('');
            setSuccess('');

            if (!formTitle.trim()) {
                setError('Form title is required');
                return;
            }

            if (questions.length === 0) {
                setError('Add at least one question');
                return;
            }

            setLoading(true);

            const formData = {
                title: formTitle.trim(),
                description: formDescription.trim(),
                formType: 'feedback',
                category: category,
                allowAnonymous: allowAnonymous,
                showProgressBar: showProgressBar,
                thankYouMessage: thankYouMessage,
                questions: questions,
                status: status,
                isPublished: isPublished,
                isLocked: false,
                updatedAt: serverTimestamp(),
            };

            if (formId && formId !== 'new') {
                await updateDoc(doc(db, 'forms', formId), formData);
                setSuccess('Form updated successfully!');
            } else {
                const newFormId = `feedback_${Date.now()}`;
                await setDoc(doc(db, 'forms', newFormId), {
                    ...formData,
                    createdAt: serverTimestamp(),
                });
                setSuccess('Form created successfully!');
            }

            setTimeout(() => {
                navigate('/admin/forms');
            }, 1500);

        } catch (err) {
            console.error('Error saving form:', err);
            setError('Failed to save form: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleImportJson = () => {
        try {
            const parsed = JSON.parse(jsonInput);

            if (!parsed.title || !Array.isArray(parsed.questions)) {
                setError('Invalid JSON format. Must include title and questions array.');
                return;
            }

            setFormTitle(parsed.title || '');
            setFormDescription(parsed.description || '');
            setCategory(parsed.category || 'general');
            setAllowAnonymous(parsed.allowAnonymous ?? false);
            setShowProgressBar(parsed.showProgressBar ?? true);
            setThankYouMessage(parsed.thankYouMessage || 'Thank you for your feedback!');
            setStatus(parsed.status || 'inactive');
            setIsPublished(parsed.isPublished ?? false);
            setQuestions(parsed.questions || []);

            setJsonDialogOpen(false);
            setSuccess('Form imported successfully!');
            setError('');

        } catch (err) {
            setError('Invalid JSON format: ' + err.message);
        }
    };

    const exportToJson = () => {
        const formJson = {
            title: formTitle,
            description: formDescription,
            formType: 'feedback',
            category: category,
            allowAnonymous: allowAnonymous,
            showProgressBar: showProgressBar,
            thankYouMessage: thankYouMessage,
            status: status,
            isPublished: isPublished,
            questions: questions,
        };

        const jsonString = JSON.stringify(formJson, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${formTitle.replace(/\s+/g, '_')}_feedback.json`;
        link.click();
    };

    const handleAddQuestion = () => {
        setError('');

        if (!newQuestion.text.trim()) {
            setError('Question text is required');
            return;
        }

        if (['checkbox', 'radio', 'dropdown'].includes(newQuestion.type) &&
            (!newQuestion.options || newQuestion.options.length === 0)) {
            setError('Add at least one option for this question type');
            return;
        }

        if (editingQuestionIndex !== null) {
            const updated = [...questions];
            updated[editingQuestionIndex] = newQuestion;
            setQuestions(updated);
        } else {
            setQuestions([...questions, newQuestion]);
        }

        setNewQuestion({
            text: '',
            type: 'rating',
            required: false,
            scale: 5,
            category: 'general',
            placeholder: '',
            maxLength: 500,
            rows: 3,
            options: [],
            labels: {
                min: '',
                max: '',
            },
        });
        setEditingQuestionIndex(null);
        setDialogOpen(false);
    };

    const handleEditQuestion = (index) => {
        setNewQuestion(questions[index]);
        setEditingQuestionIndex(index);
        setDialogOpen(true);
        setTabValue(0);
    };

    const handleDeleteQuestion = (index) => {
        if (window.confirm('Delete this question?')) {
            setQuestions(questions.filter((_, i) => i !== index));
        }
    };

    const handleAddOption = () => {
        setNewQuestion({
            ...newQuestion,
            options: [...(newQuestion.options || []), ''],
        });
    };

    const handleOptionChange = (index, value) => {
        const updated = [...(newQuestion.options || [])];
        updated[index] = value;
        setNewQuestion({ ...newQuestion, options: updated });
    };

    const handleDeleteOption = (index) => {
        if ((newQuestion.options || []).length <= 1) {
            setError('At least one option is required');
            return;
        }
        const updated = (newQuestion.options || []).filter((_, i) => i !== index);
        setNewQuestion({ ...newQuestion, options: updated });
    };

    return (
        <AdminLayout title="Form Builder">
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
                    {error && (
                        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
                            {success}
                        </Alert>
                    )}

                    {/* Header */}
                    <Box sx={{ mb: 4 }}>
                        <Button
                            startIcon={<BackIcon />}
                            onClick={() => navigate('/admin/forms')}
                            sx={{ mb: 2 }}
                        >
                            Back to Forms
                        </Button>
                        <Typography variant="h4" sx={{ fontWeight: 600, color: '#003366', mb: 1 }}>
                            💬 {formId && formId !== 'new' ? 'Edit' : 'Create'} Feedback Form
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Collect valuable feedback, ratings, and testimonials from your users
                        </Typography>
                    </Box>

                    {/* Form Details */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                            📋 Form Settings
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Form Title *"
                                    value={formTitle}
                                    onChange={(e) => setFormTitle(e.target.value)}
                                    placeholder="e.g., Customer Satisfaction Survey"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Form Description"
                                    value={formDescription}
                                    onChange={(e) => setFormDescription(e.target.value)}
                                    placeholder="Brief description of this feedback form"
                                    multiline
                                    rows={3}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Category</InputLabel>
                                    <Select
                                        value={category}
                                        onChange={(e) => setCategory(e.target.value)}
                                        label="Category"
                                    >
                                        <MenuItem value="general">General</MenuItem>
                                        <MenuItem value="customer-service">Customer Service</MenuItem>
                                        <MenuItem value="product">Product Feedback</MenuItem>
                                        <MenuItem value="event">Event Feedback</MenuItem>
                                        <MenuItem value="satisfaction">Satisfaction</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                        label="Status"
                                    >
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="inactive">Inactive</MenuItem>
                                        <MenuItem value="draft">Draft</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    fullWidth
                                    label="Thank You Message"
                                    value={thankYouMessage}
                                    onChange={(e) => setThankYouMessage(e.target.value)}
                                    placeholder="Message shown after submission"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={isPublished}
                                                onChange={(e) => setIsPublished(e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label={isPublished ? '✅ Published' : '🔒 Unpublished'}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={allowAnonymous}
                                                onChange={(e) => setAllowAnonymous(e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Allow Anonymous Responses"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={showProgressBar}
                                                onChange={(e) => setShowProgressBar(e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Show Progress Bar"
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/admin/forms')}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSaveForm}
                            disabled={loading}
                            sx={{
                                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #3d8fd9 0%, #00d5e0 100%)',
                                },
                            }}
                        >
                            {loading ? 'Saving...' : 'Save Form'}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<CodeIcon />}
                            onClick={() => setJsonDialogOpen(true)}
                            color="secondary"
                        >
                            Import JSON
                        </Button>
                        {questions.length > 0 && (
                            <Button
                                variant="outlined"
                                startIcon={<UploadIcon />}
                                onClick={exportToJson}
                                color="success"
                            >
                                Export JSON
                            </Button>
                        )}
                    </Box>

                    {/* Questions Section */}
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                ❓ Questions ({questions.length})
                            </Typography>
                            {questions.length > 0 && (
                                <Chip
                                    label={`${questions.filter(q => q.required).length} Required`}
                                    color="warning"
                                    variant="outlined"
                                />
                            )}
                        </Box>

                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                setNewQuestion({
                                    text: '',
                                    type: 'rating',
                                    required: false,
                                    scale: 5,
                                    category: 'general',
                                    placeholder: '',
                                    maxLength: 500,
                                    rows: 3,
                                    options: [],
                                    labels: {
                                        min: '',
                                        max: '',
                                    },
                                });
                                setEditingQuestionIndex(null);
                                setDialogOpen(true);
                                setTabValue(0);
                                setError('');
                            }}
                            sx={{
                                mb: 2,
                                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                            }}
                        >
                            Add Question
                        </Button>

                        <Grid container spacing={2}>
                            {questions.map((question, index) => (
                                <Grid item xs={12} key={index}>
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            borderLeft: `4px solid ${question.required ? '#ff9800' : '#4facfe'}`,
                                        }}
                                    >
                                        <CardContent>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                <Typography variant="h6" sx={{ mb: 1, flex: 1 }}>
                                                    {index + 1}. {question.text}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    {question.required && (
                                                        <Chip
                                                            label="Required"
                                                            size="small"
                                                            color="warning"
                                                        />
                                                    )}
                                                    <Chip
                                                        label={FEEDBACK_TYPES.find(t => t.value === question.type)?.label || question.type}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </Box>
                                            </Box>

                                            {question.type === 'rating' && (
                                                <Typography variant="body2" color="text.secondary">
                                                    ⭐ Scale: 1-{question.scale}
                                                </Typography>
                                            )}

                                            {question.type === 'nps' && (
                                                <Typography variant="body2" color="text.secondary">
                                                    📊 NPS Score: 0-10
                                                </Typography>
                                            )}

                                            {['checkbox', 'radio', 'dropdown'].includes(question.type) && question.options && (
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Options: {question.options.join(', ')}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </CardContent>
                                        <CardActions>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditQuestion(index)}
                                                color="primary"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteQuestion(index)}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>

                        {questions.length === 0 && (
                            <Paper
                                elevation={0}
                                sx={{
                                    py: 6,
                                    textAlign: 'center',
                                    backgroundColor: alpha('#4facfe', 0.05),
                                    border: '2px dashed',
                                    borderColor: alpha('#4facfe', 0.2),
                                    borderRadius: 2,
                                }}
                            >
                                <Typography variant="h6" color="text.secondary">
                                    No questions added yet
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Click "Add Question" to start building your feedback form
                                </Typography>
                            </Paper>
                        )}
                    </Box>

                    {/* Question Dialog */}
                    <Dialog
                        open={dialogOpen}
                        onClose={() => setDialogOpen(false)}
                        maxWidth="md"
                        fullWidth
                    >
                        <DialogTitle>
                            {editingQuestionIndex !== null ? 'Edit Question' : 'Add Question'}
                        </DialogTitle>
                        <DialogContent>
                            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
                                <Tab label="Basic" />
                                <Tab label="Options" />
                                <Tab label="Settings" />
                            </Tabs>

                            {/* Tab 0: Basic */}
                            {tabValue === 0 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                                    <TextField
                                        fullWidth
                                        label="Question Text *"
                                        value={newQuestion.text}
                                        onChange={(e) => setNewQuestion({ ...newQuestion, text: e.target.value })}
                                        multiline
                                        rows={2}
                                        placeholder="Enter your feedback question"
                                    />

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Question Type</InputLabel>
                                                <Select
                                                    value={newQuestion.type}
                                                    onChange={(e) => setNewQuestion({ ...newQuestion, type: e.target.value })}
                                                    label="Question Type"
                                                >
                                                    {FEEDBACK_TYPES.map((type) => (
                                                        <MenuItem key={type.value} value={type.value}>
                                                            {type.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Category</InputLabel>
                                                <Select
                                                    value={newQuestion.category}
                                                    onChange={(e) => setNewQuestion({ ...newQuestion, category: e.target.value })}
                                                    label="Category"
                                                >
                                                    <MenuItem value="general">General</MenuItem>
                                                    <MenuItem value="satisfaction">Satisfaction</MenuItem>
                                                    <MenuItem value="quality">Quality</MenuItem>
                                                    <MenuItem value="service">Service</MenuItem>
                                                    <MenuItem value="experience">Experience</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>

                                    {newQuestion.type === 'rating' && (
                                        <FormControl fullWidth>
                                            <InputLabel>Rating Scale</InputLabel>
                                            <Select
                                                value={newQuestion.scale}
                                                onChange={(e) => setNewQuestion({ ...newQuestion, scale: e.target.value })}
                                                label="Rating Scale"
                                            >
                                                <MenuItem value={3}>1-3 (Good/Neutral/Bad)</MenuItem>
                                                <MenuItem value={5}>1-5 (Standard Stars)</MenuItem>
                                                <MenuItem value={10}>1-10 (Detailed Scale)</MenuItem>
                                            </Select>
                                        </FormControl>
                                    )}

                                    {['text', 'textarea'].includes(newQuestion.type) && (
                                        <>
                                            <TextField
                                                fullWidth
                                                label="Placeholder Text"
                                                value={newQuestion.placeholder}
                                                onChange={(e) => setNewQuestion({ ...newQuestion, placeholder: e.target.value })}
                                                placeholder="Hint text for users"
                                            />
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label="Max Length"
                                                value={newQuestion.maxLength}
                                                onChange={(e) => setNewQuestion({ ...newQuestion, maxLength: parseInt(e.target.value) || 500 })}
                                                inputProps={{ min: 10, max: 5000 }}
                                            />
                                        </>
                                    )}

                                    {newQuestion.type === 'textarea' && (
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Number of Rows"
                                            value={newQuestion.rows}
                                            onChange={(e) => setNewQuestion({ ...newQuestion, rows: parseInt(e.target.value) || 3 })}
                                            inputProps={{ min: 2, max: 10 }}
                                        />
                                    )}

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={newQuestion.required}
                                                onChange={(e) => setNewQuestion({ ...newQuestion, required: e.target.checked })}
                                            />
                                        }
                                        label="Required Question"
                                    />
                                </Box>
                            )}

                            {/* Tab 1: Options */}
                            {tabValue === 1 && ['checkbox', 'radio', 'dropdown'].includes(newQuestion.type) && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                                        Add options for users to select
                                    </Typography>
                                    {(newQuestion.options || []).map((option, idx) => (
                                        <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                            <TextField
                                                fullWidth
                                                label={`Option ${idx + 1}`}
                                                value={option}
                                                onChange={(e) => handleOptionChange(idx, e.target.value)}
                                                size="small"
                                            />
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteOption(idx)}
                                                color="error"
                                                disabled={(newQuestion.options || []).length === 1}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    ))}
                                    <Button
                                        onClick={handleAddOption}
                                        fullWidth
                                        startIcon={<AddIcon />}
                                        variant="outlined"
                                    >
                                        Add Option
                                    </Button>
                                </Box>
                            )}

                            {/* Tab 2: Settings */}
                            {tabValue === 2 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                                    {(newQuestion.type === 'rating' || newQuestion.type === 'nps') && (
                                        <>
                                            <TextField
                                                fullWidth
                                                label="Minimum Label (Optional)"
                                                value={newQuestion.labels?.min || ''}
                                                onChange={(e) => setNewQuestion({
                                                    ...newQuestion,
                                                    labels: { ...newQuestion.labels, min: e.target.value }
                                                })}
                                                placeholder="e.g., Very Dissatisfied"
                                            />
                                            <TextField
                                                fullWidth
                                                label="Maximum Label (Optional)"
                                                value={newQuestion.labels?.max || ''}
                                                onChange={(e) => setNewQuestion({
                                                    ...newQuestion,
                                                    labels: { ...newQuestion.labels, max: e.target.value }
                                                })}
                                                placeholder="e.g., Very Satisfied"
                                            />
                                        </>
                                    )}
                                    <Alert severity="info">
                                        Labels help users understand what each rating means
                                    </Alert>
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => { setDialogOpen(false); setError(''); }}>
                                Cancel
                            </Button>
                            <Button onClick={handleAddQuestion} variant="contained">
                                {editingQuestionIndex !== null ? 'Update' : 'Add'}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* JSON Import Dialog */}
                    <Dialog
                        open={jsonDialogOpen}
                        onClose={() => setJsonDialogOpen(false)}
                        maxWidth="md"
                        fullWidth
                    >
                        <DialogTitle>Import Feedback Form from JSON</DialogTitle>
                        <DialogContent>
                            <TextField
                                fullWidth
                                multiline
                                rows={15}
                                value={jsonInput}
                                onChange={(e) => setJsonInput(e.target.value)}
                                placeholder="Paste your JSON here..."
                                variant="outlined"
                                sx={{ mt: 2, fontFamily: 'monospace' }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                📝 Paste a valid feedback form JSON. See documentation for format.
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setJsonDialogOpen(false)}>Cancel</Button>
                            <Button onClick={handleImportJson} variant="contained" color="primary">
                                Import
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Paper>
            </Container>
        </AdminLayout>
    );
}
